'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { buildWhereParams, storeApiResources } from '@eliadmin/core/src/api/store-list-request';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { LuMenu } from 'react-icons/lu';

const normalizeSidebarCategory = (category: CategoriesEntity): CategoriesEntity => ({
  ...category,
  has_childs: category.has_childs ?? Boolean(category.childs?.length),
  childs: category.childs ?? null,
});

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const locale = useLocaleStore(state => state.currentLocale);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = Boolean(category.has_childs ?? category.childs?.length);
  const localChildren = category.childs ?? [];
  const shouldFetchChildren = hasChildren && isExpanded && localChildren.length === 0;

  const { data: fetchedChildren = [], isLoading } = useQuery({
    queryKey: ['mobile-sidebar-category-children', locale, category.id],
    enabled: shouldFetchChildren,
    staleTime: 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          childs: true,
          ...buildWhereParams({ parent: { equals: category.id } }, storeApiResources.storeCategories),
          image: true,
          page: 1,
          results: 100,
        },
      }).then(res => res.data.map(normalizeSidebarCategory)),
  });

  const children = localChildren.length > 0 ? localChildren : fetchedChildren;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left font-semibold',
              layer === 1 && 'text-sm font-medium',
              layer && layer > 1 && 'text-xs font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger
            className="h-5 w-5"
            iconstyles="w-5 h-5 text-primary flex-1"
            onClick={() => setIsExpanded(current => !current)}
          />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="mx-3 my-2 h-4 rounded bg-muted" />
              ))
            : children.map((child, index) => (
                <CategoryItem
                  key={child.slug ?? index}
                  category={child}
                  layer={layer ? layer + 1 : 1}
                />
              ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  isHomepage?: boolean;
};

const MobileSidebar = (props: MobileSidebarProps) => {
  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu" className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}>
          <LuMenu size={24} className={cn(props.isHomepage ? 'text-white' : 'text-secondary')} />
        </SheetTrigger>
        <SheetContent
          side={'top'}
          className={cn(
            'mx-auto mt-3 h-fit w-[calc(100%-48px)] rounded-[16px] bg-white',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          // Without this, closing the sheet returns focus to the hamburger
          // trigger near the top of the page, which forces the browser to
          // scroll it into view — cutting off an in-progress anchor-link
          // scroll (e.g. clicking "Aktivity" → #aktivity) partway through.
          onCloseAutoFocus={e => e.preventDefault()}
          closeIcon={{
            wrapper: cn(
              'right-4 top-4 absolute w-10 h-10 border border-[#DAD7D1] rounded-full flex items-center justify-center',
              'ea-mobile-sidebar__icon-wrapper',
            ),
            icon: 'text-secondary h-4 w-4 font-bold',
          }}
        >
          <SheetTitle className="hidden"></SheetTitle>
          <div className="relative flex w-full flex-col items-start justify-start gap-6">
            {props.menu?.map(field => (
              <SheetClose
                className="text-[28px] font-bold tracking-tight text-secondary"
                asChild
                key={field.name}
              >
                <Link prefetchOnHover href={getSafeHref(field?.path ?? '#')}>{field.name}</Link>
              </SheetClose>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
